import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Stack, useTheme } from "@mui/material";
import { useTranslation } from 'react-i18next';

import SecurityIcon from "@mui/icons-material/Security";
import VerifiedUser from "@mui/icons-material/VerifiedUser";

import SecurityDialog from "components/dialogs/SecurityDialog";
import APIRequest from "system/APIRequest";
import { InfegyUser } from "system/InfegyUser";
import PasswordConfirmation from "./PasswordConfirmation";
import { useSnackbar } from "notistack";

enum ChangePasswordState {
  Idle,
  ConfirmPassword,
  ChangingPassword,
  Success
}

interface ChangePasswordProps {
  /**
   * If `true`, the component is shown.
   * @default false
   */
  isOpen: boolean;
  /**
   * The signed in user.
   */
  user: InfegyUser;
  /**
   * Callback fired when the component requests to be closed.
   */
  onClose: () => void;
  /**
   * Callback fired when the component successfully changed the user's password.
   */
  onSuccess: () => void;
}

export default function ChangePassword(props: ChangePasswordProps): React.JSX.Element {
  const theme = useTheme();
  const { i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [state, setState] = useState(ChangePasswordState.Idle);
  const [securityErrorMessage, setSecurityErrorMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const [isValid, setIsValid] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

  function handleOnClose(): void {
    props.onClose();
  };

  function handlePasswordChange(isValid: boolean, password: string, confirm: string): void {
    setIsValid(isValid);
    setNewPassword(password);
    setNewPasswordConfirm(confirm);
  }

  function handlePasswordVisibilityChange(showPassword: boolean, showPasswordConfirm: boolean): void {
    setShowPassword(showPassword);
    setShowPasswordConfirm(showPasswordConfirm);
  }

  function handleShowSecurityDialog(): void {
    setState(ChangePasswordState.ConfirmPassword);
  }

  function handleCloseSecurityDialog(): void {
    setState(ChangePasswordState.Idle);
  };

  async function handleChangePassword(currentPassword: string): Promise<void> {
    const body = {
      current_password: currentPassword,
      password: newPassword
    };
    try {
      setState(ChangePasswordState.ChangingPassword);
      await APIRequest(`obj/user/${props.user.id}`, {}, {
        method: "PUT",
        body: JSON.stringify(body)
      });
      setState(ChangePasswordState.Success);
      // Buffer the success to show UI feedback
      setTimeout(() => {
        enqueueSnackbar(i18n.t("change-password.change-password-success"), { variant: "success" });
        props.onSuccess();
      }, 1000);
    } catch (e: any) {
      if (e.code === 401 || e.code === 403) {
        // This is a failure to authenticate.  The user provided an invalid current password.
        setState(ChangePasswordState.ConfirmPassword);
        setSecurityErrorMessage(e.server_message);
      } else {
        // Some other error happened and we should still display feedback to the user.
        setState(ChangePasswordState.Idle);
        setErrorMessage(e.server_message);
      }
    }
  }

  function getChangePasswordButtonIcon(state: ChangePasswordState): React.JSX.Element | null {
    switch (state) {
      case ChangePasswordState.Idle:
      case ChangePasswordState.ConfirmPassword:
      // It seems that MUI wants a button to be set, even if the button is not used, when the
      // loading spinner is being displayed.  Without the button set, the loading spinner will
      // display on top of the text of the button instead of next to it.
      case ChangePasswordState.ChangingPassword:
        return <SecurityIcon />;
      case ChangePasswordState.Success:
        return <VerifiedUser />;
    }
  }

  useEffect(() => {
    if (props.isOpen) {
      // Every time the component is opened, make sure it is reset.
      setState(ChangePasswordState.Idle);
      setIsValid(false);
      setNewPassword("");
      setNewPasswordConfirm("");
      setShowPassword(false);
      setShowPasswordConfirm(false);
    }
  }, [props.isOpen]);

  return (
    <>
      <Box>{i18n.t('change-password.instructions')}</Box>
      <PasswordConfirmation
        onPasswordChange={handlePasswordChange}
        onVisibilityChange={handlePasswordVisibilityChange}
        password={newPassword}
        passwordConfirm={newPasswordConfirm}
        showPassword={showPassword}
        showPasswordConfirm={showPasswordConfirm}
      />
      {errorMessage && <Alert variant="filled" severity="error">{errorMessage}</Alert>}
      <Stack
        direction="row"
        spacing={1}
        sx={{
          justifyContent: "end",
          marginTop: 1
        }}>
        <Button variant="contained" color="secondary" onClick={handleOnClose}>{i18n.t('cancel')}</Button>
        <Button
            disabled={!isValid}
            loading={state === ChangePasswordState.ChangingPassword}
            loadingPosition="start"
            onClick={handleShowSecurityDialog}
            startIcon={getChangePasswordButtonIcon(state)}
            variant="contained" >
            {i18n.t('change-password.continue-button')}
          </Button>
      </Stack>
      {state === ChangePasswordState.ConfirmPassword &&
        <SecurityDialog isOpen={true} errorMessage={securityErrorMessage} onSubmit={handleChangePassword} onClose={handleCloseSecurityDialog} />
      }
    </>
  );
}
